import {
  Image,
  ScrollView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Box, Text } from '@/components/ui/primitives';
import { config } from '@/config/env';
import { getAuthHref } from '@/lib/routes';

const features = [
  {
    tag: 'ODDS-01',
    title: 'Underdogs hit harder',
    body: 'Odds-based scoring turns bold picks into visible separation on the board.',
  },
  {
    tag: 'LEAGUE-02',
    title: 'Commissioner control',
    body: 'Build private leagues, invite the room, and keep the season moving from one workstation.',
  },
  {
    tag: 'PICKS-03',
    title: 'Every slate on register',
    body: 'Weekly picks, spreads, totals, and standings stay organized without spreadsheet drift.',
  },
];

export default function LandingScreen() {
  const router = useRouter();
  const emailPasswordEnabled = config.auth.emailPasswordEnabled;
  const canUseGoogleSignIn = config.socialAuth.googleEnabled;
  const shouldFastTrackGoogle = !emailPasswordEnabled && canUseGoogleSignIn;

  const handlePrimaryCta = () => {
    if (shouldFastTrackGoogle) {
      router.push(getAuthHref({ startGoogleSignIn: true }));
      return;
    }
    router.push(getAuthHref());
  };

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
      <Box style={styles.gridLayer} />

      <Box style={styles.masthead}>
        <Box style={styles.logoPlate}>
          <Image
            source={require('../../assets/pckm-logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </Box>

        <Box style={styles.heroCopy}>
          <Text variant="caption" style={styles.kicker}>
            PCKM / NFL PICK&apos;EM OPS
          </Text>
          <Box style={styles.headlineWrap}>
            <Box style={styles.acidSlab} />
            <Text variant="h1" style={styles.headline}>
              Own the board.
            </Text>
          </Box>
          <Text variant="body" style={styles.subhead}>
            Run the pool, price the risk, and make every weekly pick feel like it was pulled
            fresh from the press.
          </Text>
        </Box>

        <Box style={styles.ctaRail}>
          <Button onPress={handlePrimaryCta} size="lg">
            {shouldFastTrackGoogle ? 'Login with Google' : 'Get Started'}
          </Button>
          <Text variant="caption" style={styles.railNote}>
            WARM PAPER / PRESS BLACK / ACID SIGNAL
          </Text>
        </Box>
      </Box>

      <Box style={styles.featureGrid}>
        {features.map((feature, index) => (
          <Box key={feature.tag} style={[styles.featureShell, index === 1 && styles.featureShellOffset]}>
            <Box style={styles.backingPlate} />
            <Card style={styles.featureCard}>
              <CardHeader style={styles.featureHeader}>
                <Text variant="caption" style={styles.featureTag}>
                  {feature.tag}
                </Text>
                <CardTitle>{feature.title}</CardTitle>
              </CardHeader>
              <CardContent style={styles.featureBody}>
                <Text variant="bodySmall" color="pressMuted">
                  {feature.body}
                </Text>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </ScrollView>
  );
}

const styles = StyleSheet.create((theme, runtime) => {
  const isWide = runtime.screen.width >= theme.breakpoints.md;

  return {
    acidSlab: {
      backgroundColor: theme.colors.acid,
      bottom: isWide ? 8 : 5,
      height: isWide ? 26 : 18,
      left: 0,
      position: 'absolute',
      right: isWide ? 42 : 10,
    },
    backingPlate: {
      backgroundColor: theme.colors.paperDark,
      borderColor: theme.colors.press,
      borderWidth: 3,
      bottom: -8,
      left: 10,
      position: 'absolute',
      right: -8,
      top: 10,
      transform: [{ rotate: '-2deg' }],
    },
    content: {
      backgroundColor: theme.colors.background,
      flexGrow: 1,
      gap: isWide ? 34 : 24,
      overflow: 'hidden',
      paddingBottom: 44,
      paddingHorizontal: isWide ? 40 : 18,
      paddingTop: isWide ? 36 : 22,
    },
    ctaRail: {
      alignSelf: isWide ? 'stretch' : 'stretch',
      gap: 14,
      justifyContent: 'center',
      minWidth: isWide ? 220 : undefined,
    },
    featureBody: {
      paddingTop: 0,
    },
    featureCard: {
      minHeight: 180,
    },
    featureGrid: {
      alignItems: 'stretch',
      flexDirection: isWide ? 'row' : 'column',
      gap: isWide ? 24 : 20,
    },
    featureHeader: {
      gap: 12,
    },
    featureShell: {
      flex: 1,
      minHeight: 190,
      position: 'relative',
    },
    featureShellOffset: {
      marginTop: isWide ? 18 : 0,
    },
    featureTag: {
      alignSelf: 'flex-start',
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.press,
      borderWidth: 2,
      color: theme.colors.press,
      paddingHorizontal: 8,
      paddingVertical: 4,
    },
    gridLayer: {
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.press,
      borderWidth: 1,
      bottom: 0,
      left: 0,
      opacity: 0.12,
      position: 'absolute',
      right: 0,
      top: 0,
      transform: [{ rotate: '-1deg' }],
    },
    headline: {
      fontSize: isWide ? 72 : 44,
      lineHeight: isWide ? 72 : 46,
      maxWidth: isWide ? 640 : 360,
    },
    headlineWrap: {
      alignSelf: 'flex-start',
      position: 'relative',
    },
    heroCopy: {
      flex: 1,
      gap: 12,
    },
    kicker: {
      color: theme.colors.pressMuted,
    },
    logo: {
      height: isWide ? 106 : 74,
      width: isWide ? 160 : 112,
    },
    logoPlate: {
      alignSelf: isWide ? 'stretch' : 'flex-start',
      backgroundColor: theme.colors.acid,
      borderColor: theme.colors.press,
      borderWidth: 3,
      justifyContent: 'center',
      minWidth: isWide ? 180 : 128,
      padding: 10,
    },
    masthead: {
      alignItems: isWide ? 'center' : 'stretch',
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.press,
      borderWidth: 5,
      elevation: 8,
      flexDirection: isWide ? 'row' : 'column',
      gap: isWide ? 26 : 18,
      padding: isWide ? 28 : 18,
      shadowColor: theme.colors.press,
      shadowOffset: { width: 14, height: 14 },
      shadowOpacity: 1,
      shadowRadius: 0,
    },
    railNote: {
      color: theme.colors.pressMuted,
      maxWidth: 220,
    },
    screen: {
      backgroundColor: theme.colors.background,
      flex: 1,
    },
    subhead: {
      color: theme.colors.pressMuted,
      maxWidth: 620,
    },
  };
});
